import React from "react";
import { motion } from "framer-motion";
import { 
  MapPin, 
  ArrowUp,
  Code,
  MessageCircle
} from "lucide-react";
import { openIntercomForSupport } from "@/utils/intercom";

const NewFooter: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const locations = [
    { city: "San Francisco", country: "USA", flag: "🇺🇸" },
    { city: "Paris", country: "France", flag: "🇫🇷" },
    { city: "Rabat", country: "Morocco", flag: "🇲🇦" }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 border-t border-gray-800/50">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500/3 rounded-full blur-3xl" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            
            {/* Company Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Code className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white">ShredsAI</h3>
                </div>
                
                <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                  The first AI for complex backend development. Build production-grade systems with unprecedented precision and speed.
                </p>


                {/* Contact Support Button */}
                <motion.button
                  onClick={openIntercomForSupport}
                  className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-blue-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Contact Support</span>
                </motion.button>
              </motion.div>
            </div>

            {/* Team Locations */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h4 className="text-white font-semibold mb-6 text-xl">Made with ❤️ in</h4>
                
                <div className="space-y-3">
                  {locations.map((location, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-500/10 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{location.city}</p>
                        <p className="text-gray-400 text-xs">{location.country}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Privacy & Legal */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <h4 className="text-white font-semibold mb-6 text-xl">Legal</h4>
                
                <div className="space-y-4">
                  <motion.a
                    href="/privacy"
                    className="block p-4 bg-gray-800/30 rounded-lg border border-gray-700/50 hover:bg-gray-800/50 transition-all duration-300 group"
                    whileHover={{ scale: 1.02, x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium group-hover:text-blue-300 transition-colors duration-300">
                          Privacy Policy
                        </p>
                        <p className="text-gray-400 text-sm">
                          Learn how we protect your data
                        </p>
                      </div>
                      <ArrowUp className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors duration-300 rotate-45" />
                    </div>
                  </motion.a>

                  <div className="p-4 bg-gray-800/20 rounded-lg border border-gray-700/30">
                    <p className="text-gray-400 text-sm">
                      <span className="text-white font-medium">contact@shreds.ai</span>
                      <br />
                      For general inquiries and support
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800/50">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <motion.p 
                className="text-gray-400 text-sm text-center md:text-left"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                © 2024 ShredsAI. All rights reserved. Built with love for developers worldwide.
              </motion.p>
              
              <motion.button
                onClick={scrollToTop}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all duration-300 group"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <span className="text-gray-400 group-hover:text-white text-sm transition-colors duration-300">
                  Back to top
                </span>
                <ArrowUp className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors duration-300" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewFooter;